import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useEffect, useState } from "react";
import { getDisplayPagesConfigurations, updateDisplayPagesConfigurations } from "../../Helpers/DisplayTemplates/DisplayPages";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";

function ObjectsAndPages({ portalId }) {

    const enabledColumns = [
        {
            title: "Table",
            databaseField: "shouldCreateTable",
            disabledField: ""
        },
        {
            title: "Split View",
            databaseField: "shouldCreateSplitView",
            disabledField: ""
        },
        {
            title: "Kanban",
            databaseField: "shouldCreateKanban",
            disabledField: "isKanbanDisabled"
        },
        {
            title: "Calendar",
            databaseField: "shouldCreateCalendar",
            disabledField: "isCalendarDisabled"
        },
        {
            title: "Cards",
            databaseField: "shouldCreateCards",
            disabledField: ""
        },
        {
            title: "Gantt Chart",
            databaseField: "shouldCreateGanttChart",
            disabledField: "isGanntChartDisabled"
        }
    ];

    const [data, setData] = useState([]);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [showEntityRemovedDialog, setShowEntityRemovedDialog] = useState(false);

    useEffect(() => {
        getDisplayPagesConfigurations(portalId)
            .then(data => {
                setData(data.map(entity => ({
                    ...entity,
                    isKanbanDisabled: entity.isUserEntity,
                    isCalendarDisabled: entity.isUserEntity,
                    isGanntChartDisabled: entity.isUserEntity,
                    defaultConfig: {
                        shouldCreateTable: entity.shouldCreateTable,
                        shouldCreateKanban: entity.shouldCreateKanban,
                        shouldCreateCards: entity.shouldCreateCards,
                        shouldCreateSplitView: entity.shouldCreateSplitView,
                        shouldCreateCalendar: entity.shouldCreateCalendar,
                        shouldCreateGanttChart: entity.shouldCreateGanttChart,
                        useInPortal: entity.useInPortal
                    }
                })))
            });
    }, [])

    useEffect(() => {
        const isDisabled = data.every(entity => {
            return Object.keys(entity.defaultConfig).every(key => {
                return entity.defaultConfig[key] === entity[key];
            });
        });
        setIsSaveDisabled(isDisabled);
    }, [data])

    function handleCheckbox(event, entityId) {
        const { name, value } = event.target;
        setData(prevData => prevData.map((entity) => {
            if (entity.entityId === entityId) {
                const newEntity = { ...entity, [name]: value };
                const isAnyPageEnabled = enabledColumns.some(column => newEntity[column.databaseField]);
                if (newEntity.useInPortal !== isAnyPageEnabled) {
                    newEntity.useInPortal = isAnyPageEnabled;
                    newEntity.shouldCreateDetailsView = isAnyPageEnabled;
                    newEntity.shouldCreateNewRecord = isAnyPageEnabled;
                    newEntity.shouldCreateEditRecord = isAnyPageEnabled;
                }
                return newEntity;
            }
            return entity;
        }))
    }

    function handleUseInPortal(event, entityId) {
        const { name, value } = event.target;
        setData(prevData => prevData.map((entity) => {
            if (entity.entityId === entityId) {
                const newEntity = {
                    ...entity,
                    [name]: value
                };
                if (value === true) {
                    return {
                        ...newEntity,
                        shouldCreateTable: true,
                        shouldCreateKanban: false,
                        shouldCreateCards: false,
                        shouldCreateSplitView: true,
                        shouldCreateCalendar: false,
                        shouldCreateGanttChart: false,
                        shouldCreateDetailsView: true,
                        shouldCreateEditView: true,
                        shouldCreateListView: true
                    }
                } else {
                    for (const column of enabledColumns) {
                        if (newEntity[column.disabledField] !== true) {
                            newEntity[column.databaseField] = value;
                        }
                    }
                }
                return newEntity;
            }
            return entity;
        }))
    }

    async function handleSave() {
        const entityWithUseInPortalChanged = data.some(entity => (
            entity.useInPortal !== entity.defaultConfig.useInPortal &&
            entity.useInPortal === false
        ));
        if (entityWithUseInPortalChanged) {
            setShowEntityRemovedDialog(true);
        } else {
            await saveConfigurations();
        }
    }

    async function saveConfigurations() {
        const updatedEntities = data.filter(entity => {
            return Object.keys(entity.defaultConfig).some(key => {
                return entity.defaultConfig[key] !== entity[key];
            });
        });
        await updateDisplayPagesConfigurations(portalId, updatedEntities);
        setData(prevData => prevData.map(entity => {
            return {
                ...entity,
                defaultConfig: {
                    shouldCreateTable: entity.shouldCreateTable,
                    shouldCreateKanban: entity.shouldCreateKanban,
                    shouldCreateCards: entity.shouldCreateCards,
                    shouldCreateSplitView: entity.shouldCreateSplitView,
                    shouldCreateCalendar: entity.shouldCreateCalendar,
                    shouldCreateGanttChart: entity.shouldCreateGanttChart,
                    useInPortal: entity.useInPortal
                }
            }
        }));
    }

    async function handleEntityRemovedDialogConfirm() {
        await saveConfigurations();
        setShowEntityRemovedDialog(false);
    }

    function revertChanges() {
        setData(prevData => prevData.map(entity => {
            return {
                ...entity,
                ...entity.defaultConfig
            }
        }))
    }

    function getNamesOfEntitiesRemovedFromPortal() {
        return data.filter(entity => entity.useInPortal !== entity.defaultConfig.useInPortal)
                    .map(entity => entity.entityName)
                    .join(", ");
    }

    return (
        <div>
            {
                showEntityRemovedDialog && (
                    <Dialog
                        width={"35%"}
                        title={`Removal of ${getNamesOfEntitiesRemovedFromPortal()} Object from Portal`}
                        onClose={() => {}}
                    >
                        <p>
                            You are about to remove the { getNamesOfEntitiesRemovedFromPortal() } object from this portal.
                            Proceeding with this action will result in the permanent deletion of all pages created for the { getNamesOfEntitiesRemovedFromPortal() } object within this portal during the next Preview/Publish.
                        </p>
                        <p>
                            Are you sure you wish to continue with the removal of the { getNamesOfEntitiesRemovedFromPortal() } object?
                            This action is irreversible.
                        </p>
                        <DialogActionsBar layout="end">
                            <button
                                className="btn"
                                onClick={() => setShowEntityRemovedDialog(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={handleEntityRemovedDialogConfirm}
                            >
                                Yes, Proceed
                            </button>
                        </DialogActionsBar>
                    </Dialog>
                )
            }
            <div className="d-flex justify-content-end w-100 p-3">
                <button
                    className="m-1 btn btn-primary me-2"
                    disabled={isSaveDisabled}
                    onClick={handleSave}
                >
                    <i className="fad fa-save"></i>
                    &nbsp;&nbsp;
                    Save
                </button>
                <button
                    className="m-1 btn btn-secondary"
                    disabled={isSaveDisabled}
                    onClick={revertChanges}
                >
                    <span className="k-icon k-i-undo pb-1"></span>
                    &nbsp;&nbsp;
                    Revert Changes
                </button>
            </div>
            <Grid
                data={data}
            >
                <GridColumn
                    title="Use In Portal"
                    cell={(props) => {
                        return (
                            <td>
                                <Checkbox
                                    name={"useInPortal"}
                                    label={props.dataItem.entityName}
                                    checked={props.dataItem.useInPortal}
                                    value={props.dataItem.useInPortal}
                                    onChange={event => {
                                        handleUseInPortal(event, props.dataItem.entityId)
                                    }}
                                />
                            </td>
                        )
                    }}
                />
                {enabledColumns.map((column, index) => {
                    return (
                        <GridColumn
                            key={index}
                            title={column.title}
                            field={column.databaseField}
                            cell={(props) => {
                                return (
                                    <td>
                                        <Checkbox
                                            aria-label={`${props.dataItem.entityId}-${column.databaseField}`}
                                            name={column.databaseField}
                                            checked={props.dataItem[column.databaseField]}
                                            value={props.dataItem[column.databaseField]}
                                            disabled={props.dataItem[column.disabledField]}
                                            onChange={event => {
                                                handleCheckbox(event, props.dataItem.entityId)
                                            }}
                                        />
                                    </td>
                                )
                            }}
                        />
                    )
                })}
            </Grid>
        </div>
    )
}

export default ObjectsAndPages;